import { Drawer, Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { APP_USER_DETAIL_ROUTE, END_USER_DETAIL_ROUTE } from "config/route-consts";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  AccessEvent,
  AccessLog,
  AccountRole,
  ACCESS_EVENT_LABEL,
  ACCOUNT_ROLE_LABEL,
  DEMO_ACCESS_LOGS,
} from "./system-log-demo-data";
import {
  DateRange,
  formatDateTime,
  getDefaultRange,
  isDefaultRange,
  Initials,
  LogEmpty,
  LogPagination,
  PAGE_SIZE,
  RangeField,
  ResetFilterButton,
  SearchField,
  SelectField,
} from "./SystemLogShared";

interface AccessFilter {
  search: string;
  range: DateRange;
  role?: AccountRole;
  event?: AccessEvent;
  result?: "success" | "failed";
}

const ROLE_OPTIONS = (Object.keys(ACCOUNT_ROLE_LABEL) as AccountRole[]).map((value) => ({
  value,
  label: ACCOUNT_ROLE_LABEL[value],
}));
const EVENT_OPTIONS = (Object.keys(ACCESS_EVENT_LABEL) as AccessEvent[]).map((value) => ({
  value,
  label: ACCESS_EVENT_LABEL[value],
}));
const RESULT_OPTIONS = [
  { value: "success" as const, label: "Thành công" },
  { value: "failed" as const, label: "Thất bại" },
];

function createFilter(): AccessFilter {
  return { search: "", range: getDefaultRange() };
}

// Lọc tại chỗ cho bản demo. Khi có API: gửi `filter` lên server, bỏ hàm này.
function filterDemo(list: AccessLog[], filter: AccessFilter) {
  const keyword = filter.search.trim().toLowerCase();
  return list.filter(
    (item) =>
      (!keyword ||
        [item.accountName, item.accountContact, item.ip].some((text) =>
          text.toLowerCase().includes(keyword)
        )) &&
      (!filter.role || item.role === filter.role) &&
      (!filter.event || item.event === filter.event) &&
      (!filter.result || item.isSuccess === (filter.result === "success"))
  );
}

function EventCell({ item }: { item: AccessLog }) {
  return (
    <>
      {item.isSuccess ? (
        ACCESS_EVENT_LABEL[item.event]
      ) : (
        <span className="status-badge status-badge--error">
          {ACCESS_EVENT_LABEL[item.event]}
        </span>
      )}
      {item.warning && (
        <div className="system-log__flag">
          <i className="tio-warning_outlined" />
          {item.warning}
        </div>
      )}
    </>
  );
}

export default function AccessLogTab() {
  const history = useHistory();
  const [filter, setFilter] = React.useState<AccessFilter>(createFilter);
  const [skip, setSkip] = React.useState(0);
  const [take, setTake] = React.useState(PAGE_SIZE);
  const [selected, setSelected] = React.useState<AccessLog>();

  const list = React.useMemo(() => filterDemo(DEMO_ACCESS_LOGS, filter), [filter]);
  const isFiltered =
    !!filter.search || !!filter.role || !!filter.event || !!filter.result || !isDefaultRange(filter.range);
  const page = list.slice(skip, skip + take);

  const handleChangeFilter = React.useCallback(
    <K extends keyof AccessFilter>(key: K) =>
      (value: AccessFilter[K]) => {
        setFilter((current) => ({ ...current, [key]: value }));
        setSkip(0);
      },
    []
  );

  const handlePagination = React.useCallback((nextSkip: number, nextTake: number) => {
    setSkip(nextSkip);
    setTake(nextTake);
  }, []);

  const handleResetFilter = React.useCallback(() => {
    setFilter(createFilter());
    setSkip(0);
  }, []);

  // Lọc bảng theo tài khoản đang xem trong panel.
  const handleFilterByAccount = React.useCallback(() => {
    if (!selected) return;
    setFilter((current) => ({ ...current, search: selected.accountContact }));
    setSkip(0);
    setSelected(undefined);
  }, [selected]);

  // Mở trang chi tiết đúng loại tài khoản đang xem trong panel.
  const handleOpenAccount = React.useCallback(() => {
    if (!selected) return;
    const route = selected.role === "admin" ? APP_USER_DETAIL_ROUTE : END_USER_DETAIL_ROUTE;
    history.push(`${route}?id=${selected.accountId}`);
  }, [history, selected]);

  const columns: ColumnProps<AccessLog>[] = React.useMemo(
    () => [
      {
        title: "Thời gian",
        dataIndex: "createdAt",
        width: 128,
        render: (value: string) => (
          <span className="tabular-nums system-log__nowrap">{formatDateTime(value)}</span>
        ),
      },
      {
        title: "Tài khoản",
        dataIndex: "accountName",
        render: (_value: string, item) => (
          <div className="system-log__obj">
            <Initials name={item.accountName} />
            <div className="system-log__cell">
              <div className="system-log__cell-main">{item.accountName}</div>
              <div className="system-log__cell-sub system-log__truncate">
                {ACCOUNT_ROLE_LABEL[item.role]} · {item.accountContact}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: "Sự kiện",
        dataIndex: "event",
        render: (_value: string, item) => <EventCell item={item} />,
      },
      {
        title: "Địa chỉ IP",
        dataIndex: "ip",
        render: (value: string) => <span className="tabular-nums">{value}</span>,
      },
      {
        title: "Thiết bị",
        dataIndex: "device",
        render: (_value: string, item) => (
          <span className="text-muted-token">
            {item.client} · {item.device}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="system-log__toolbar">
        <SearchField
          placeholder="Tìm tên, email, SĐT hoặc IP"
          value={filter.search}
          onChange={handleChangeFilter("search")}
        />
        <RangeField value={filter.range} onChange={handleChangeFilter("range")} />
        <SelectField
          placeholder="Mọi tài khoản"
          value={filter.role}
          options={ROLE_OPTIONS}
          onChange={handleChangeFilter("role")}
        />
        <SelectField
          placeholder="Mọi sự kiện"
          value={filter.event}
          options={EVENT_OPTIONS}
          onChange={handleChangeFilter("event")}
        />
        <SelectField
          placeholder="Mọi kết quả"
          value={filter.result}
          options={RESULT_OPTIONS}
          onChange={handleChangeFilter("result")}
        />
        <ResetFilterButton visible={isFiltered} onClick={handleResetFilter} />
      </div>

      {list.length === 0 ? (
        <LogEmpty
          title={
            isFiltered
              ? "Không có lượt truy cập nào khớp bộ lọc"
              : "Không có lượt truy cập nào trong khoảng thời gian này"
          }
          onReset={handleResetFilter}
        />
      ) : (
        <>
          <Table
            className="system-log__table"
            rowKey="id"
            columns={columns}
            dataSource={page}
            pagination={false}
            scroll={{ x: "max-content" }}
            rowClassName={() => "system-log__row-click"}
            onRow={(item) => ({ onClick: () => setSelected(item) })}
          />
          <ul className="system-log__rows">
            {page.map((item) => (
              <li key={item.id} className="system-log__row-click" onClick={() => setSelected(item)}>
                <Initials name={item.accountName} />
                <div className="system-log__grow">
                  <div className="system-log__rows-top">
                    <span className="system-log__cell-main">{item.accountName}</span>
                    <span className="system-log__cell-sub tabular-nums">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                  <div>
                    <EventCell item={item} />
                  </div>
                  <div className="system-log__cell-sub">
                    {item.ip} · {item.client} · {item.device}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <LogPagination
            skip={skip}
            take={take}
            total={list.length}
            unit="lượt truy cập"
            onChange={handlePagination}
          />
        </>
      )}

      <Drawer
        className="system-log__drawer"
        visible={!!selected}
        width={420}
        closable={false}
        onClose={() => setSelected(undefined)}
        title={
          selected && (
            <div className="system-log__drawer-head">
              <div>
                <div className="system-log__drawer-title">{ACCESS_EVENT_LABEL[selected.event]}</div>
                <div className="system-log__cell-sub">
                  {moment(selected.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                </div>
              </div>
              <button
                type="button"
                className="system-log__icon-btn"
                aria-label="Đóng"
                onClick={() => setSelected(undefined)}
              >
                <i className="tio-clear" />
              </button>
            </div>
          )
        }
        footer={
          <div className="system-log__drawer-foot">
            <button type="button" className="system-log__btn" onClick={handleOpenAccount}>
              Xem tài khoản
            </button>
            <button type="button" className="system-log__btn" onClick={handleFilterByAccount}>
              Lọc theo tài khoản này
            </button>
          </div>
        }
      >
        {selected && (
          <>
            <div className="system-log__obj system-log__drawer-account">
              <Initials name={selected.accountName} size="lg" />
              <div className="system-log__cell">
                <div className="system-log__cell-main">{selected.accountName}</div>
                <div className="system-log__cell-sub">
                  {ACCOUNT_ROLE_LABEL[selected.role]} · {selected.accountContact}
                </div>
              </div>
            </div>
            <dl className="system-log__dl">
              <div>
                <dt>Kết quả</dt>
                <dd>
                  <span
                    className={`status-badge status-badge--${selected.isSuccess ? "success" : "error"}`}
                  >
                    {selected.isSuccess ? "Thành công" : "Thất bại"}
                  </span>
                </dd>
              </div>
              {selected.reason && (
                <div>
                  <dt>Lý do</dt>
                  <dd>{selected.reason}</dd>
                </div>
              )}
              {selected.warning && (
                <div>
                  <dt>Cảnh báo</dt>
                  <dd>
                    <span className="status-badge status-badge--warning">{selected.warning}</span>
                  </dd>
                </div>
              )}
              <div>
                <dt>Địa chỉ IP</dt>
                <dd className="tabular-nums">{selected.ip}</dd>
              </div>
              <div>
                <dt>Thiết bị</dt>
                <dd>{selected.device}</dd>
              </div>
              <div>
                <dt>Ứng dụng</dt>
                <dd>{selected.client}</dd>
              </div>
            </dl>
          </>
        )}
      </Drawer>
    </>
  );
}
